import { userLogout } from '@/actions/authActions';
import AppModal from '@/components/common/AppModal';
import { LoginWindowState } from '@/state';
import { useDispatch, useSelector } from '@/state/store';
import { AuthProvider, startOAuth2Flow } from '@/utils/pkce';
import { stateLoginWindow, stateUser } from '@/selectors/selectors';
import { ActionType } from '@/actions/actiontypes';
import { runtimeEnvironment } from '@/utils/runtimeEnvironment';

interface LoginFormProps {
    handleWindowChange: (window: LoginWindowState) => void;
    isLoggedIn: boolean;
}

export const LoginForm = ({ handleWindowChange, isLoggedIn }: LoginFormProps) => {
    const dispatch = useDispatch();
    const loginState = useSelector(stateLoginWindow);
    const userState = useSelector(stateUser);

    const handleLogin = (provider: AuthProvider) => {
        if (userState.loginError) {
            dispatch({ type: ActionType.LOGOUT });
        }
        startOAuth2Flow(provider);
    };

    const onLogout = () => {
        handleWindowChange(LoginWindowState.NONE);
        dispatch(userLogout());
    };

    const onClose = () => {
        if (userState.loginError) {
            dispatch({ type: ActionType.LOGOUT });
        }
        handleWindowChange(LoginWindowState.NONE);
    };

    return (
        <>
            <AppModal
                show={loginState === LoginWindowState.LOGIN}
                title="Tilgang til sensitive data"
                onClose={onClose}
                className="c-login"
            >
                {runtimeEnvironment() === 'test' && (
                    <div className="c-login__test-warning">
                        <p>Du må være på SVV-nettverket for å logge inn i testmiljøet.</p>
                    </div>
                )}
                {!isLoggedIn ? (
                    <>
                        {userState.loginError && (
                            <div className="c-login__error">
                                <p className="c-login__error-message">
                                    Ugyldig bruker, brukeren mangler SVV roller. For mer
                                    informasjon se{' '}
                                    <a
                                        href="https://www.nvdb.no/dataforvaltning-og-kvalitet/datakatalogen/data-som-ikke-er-apent-publisert/#Tilgang-sensitive-egenskapstyper"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="c-login__error-link"
                                    >
                                        nvdb.no
                                    </a>
                                </p>
                            </div>
                        )}
                        <div>
                            <p className="c-login__paragraph">
                                Sensitive data er tilgjengelig i Vegkart ved pålogging for
                                brukere med et dokumentert faglig behov. Innlogget bruker
                                er ansvarlig for at sensitive data ikke videreformidles
                                eller misbrukes.
                            </p>
                            <p>Logg inn som:</p>
                            <div className="c-login__buttons" style={{ display: 'flex' }}>
                                <button
                                    type="button"
                                    onClick={() => handleLogin('employee')}
                                    className="dark-button"
                                >
                                    Ansatt/innleid i SVV
                                </button>
                                <button
                                    type="button"
                                    onClick={() => handleLogin('idporten')}
                                    className="dark-button"
                                >
                                    Andre brukere (ID-porten)
                                </button>
                            </div>
                        </div>
                    </>
                ) : (
                    <>
                        <p className="c-login__paragraph">
                            Du er allerede logget inn. Ønsker du å logge ut?
                        </p>
                        <div style={{ display: 'flex' }}>
                            <button
                                id="logoutButton"
                                type="button"
                                onClick={onLogout}
                                className="dark-button"
                                style={{ marginTop: 0 }}
                            >
                                Logg ut
                            </button>
                        </div>
                    </>
                )}
                <div className="c-login__footer">
                    <hr />
                    <p>
                        Trenger du tilgang eller ønsker mer informasjon om sensitive data?{' '}
                        <a
                            href="https://www.nvdb.no/dataforvaltning-og-kvalitet/data-som-ikke-er-apent-publisert"
                            target="_blank"
                            rel="noreferrer noopener"
                        >
                            Finn informasjon på nvdb.no.
                        </a>
                    </p>
                </div>
            </AppModal>
        </>
    );
};
